/**
 * Datafa.st analytics utilities
 *
 * Datafast handles revenue attribution — connecting marketing channels to paying customers.
 * For behavioral analytics, use PostHog via lib/analytics.ts.
 *
 * Datafast goals count toward the 10k/month event limit.
 * Only track high-value conversion events here.
 */

import { trackEvent, ClipToolAnalytics } from "./analytics";

interface IdentifyProps {
  userId: string;
  name?: string;
  email?: string;
  image?: string;
  plan?: string;
}

/**
 * Identify a user with Datafa.st (call after login)
 */
export function identifyUser(props: IdentifyProps): void {
  if (typeof window === "undefined") return;

  window.datafast?.("identify", {
    user_id: props.userId,
    name: props.name,
    email: props.email,
    image: props.image,
    plan: props.plan,
  });
}

/**
 * Track a custom goal in Datafast only
 */
export function trackGoal(
  name: string,
  props?: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  window.datafast?.(name, props);
}

/**
 * Track a conversion event in BOTH Datafast (revenue attribution) and PostHog (behavioral).
 * Use this for key funnel steps that need to appear in both systems.
 */
export function trackConversion(
  name: string,
  props?: Record<string, unknown>
): void {
  trackGoal(name, props);
  trackEvent(name, props);
}

// =============================================================================
// Conversion Goal Helpers (Datafast + PostHog dual-tracked)
// =============================================================================

export function dfTrackSignup(props?: { method?: string }): void {
  trackConversion("signup", props);
}

export function dfTrackPricingViewed(props?: {
  source?: string;
}): void {
  trackConversion("pricing_viewed", props);
}

export function dfTrackCheckoutStarted(props: {
  plan_id: string;
  billing_period?: string;
}): void {
  trackConversion("checkout_started", props);
}

export function dfTrackCtaClicked(props: {
  location: string;
  text?: string;
}): void {
  trackGoal("cta_clicked", props);
}

export function dfTrackVideoProcessed(props: {
  video_id?: string;
  is_first_video?: boolean;
}): void {
  trackConversion("video_processed", props);
}

/**
 * An export entry point was clicked. Dual-tracked like the conversion it pairs
 * with, so a funnel can compare attempts against exports on one boundary
 * instead of inferring the attempt from the export.
 */
export function dfTrackClipExportClicked(props: {
  source: string;
  tool_slug?: string;
  clip_id?: string;
}): void {
  trackGoal("clip_export_clicked", props);
  ClipToolAnalytics.exportClicked(props.source, { toolSlug: props.tool_slug });
}

export function dfTrackClipExported(props: {
  clip_id?: string;
  is_first_export?: boolean;
}): void {
  trackConversion("clip_exported", props);
}

export function dfTrackPaywallHit(props: {
  credit_type: string;
  current_plan?: string;
  action_attempted?: string;
}): void {
  trackConversion("paywall_hit", props);
}

// =============================================================================
// Cookie Utilities
// =============================================================================

/**
 * Get a cookie value by name
 */
const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift();
  }
  return undefined;
};

/**
 * Get Datafast metadata for checkout sessions.
 * Returns visitor and session IDs for revenue attribution.
 * @see https://datafa.st/docs/stripe-checkout-api
 */
export const getDatafastMetadata = (): Record<string, string | undefined> => ({
  datafast_visitor_id: getCookie("datafast_visitor_id"),
  datafast_session_id: getCookie("datafast_session_id"),
});
